import { useEffect } from 'react';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';

import { useAuthContext } from 'src/auth/hooks';

import { useResponsive } from 'src/hooks/use-responsive';

import Scrollbar from 'src/components/scrollbar';

import NavSection from 'src/layouts/dashboard/components/nav-section';

import { usePathname } from 'src/routes/hooks';

import { maxLine } from 'src/theme/styles';

import { useNavData } from '../config-navigation';

import { NAV } from '../../config-layout';

// ----------------------------------------------------------------------

type Props = {
  openNav: boolean;
  onCloseNav: VoidFunction;
};

export default function NavVertical({ openNav, onCloseNav }: Props) {
  const pathname = usePathname();
  const { user: currentUser } = useAuthContext();

  const lgUp = useResponsive('up', 'lg');

  const navData = useNavData();

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 3, pr: 2, py: 1.25 }}>
        <Avatar
          variant="rounded"
          alt="Avatar"
          src={
            currentUser?.currentRole?.currentRole?.organizeData?.imageUrl ||
            'https://placehold.co/48x48'
          }
          sx={{ width: { sx: 32, md: 48 }, height: { sx: 32, md: 48 } }}
        />

        <ListItemText
          primary={currentUser?.organizeData?.name || 'No Organization'}
          secondary="Khaya Dashboard"
          primaryTypographyProps={{
            typography: 'h5',
            fontWeight: 'bold',
            lineHeight: '1.1',
            sx: { ...maxLine({ line: 2 }) },
          }}
          secondaryTypographyProps={{
            component: 'span',
            typography: 'body2',
            color: 'text.disabled',
            mt: 0.5,
          }}
        />
      </Box>
      <Scrollbar
        sx={{
          height: 1,
          '& .simplebar-content': {
            height: 1,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <NavSection
          data={navData}
          slotProps={{
            currentRole: currentUser?.currentRole?.role,
          }}
        />

        <Box sx={{ flexGrow: 1 }} />
      </Scrollbar>
    </>
  );

  return (
    <Box
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.W_VERTICAL },
      }}
    >
      {lgUp ? (
        <Stack
          sx={{
            height: 1,
            position: 'fixed',
            width: NAV.W_VERTICAL,
            borderRight: (theme) => `dashed 1px ${theme.palette.divider}`,
          }}
        >
          {renderContent}
        </Stack>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          PaperProps={{
            sx: {
              width: NAV.W_VERTICAL,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
